'use client'
import { MailIcon } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { authClient } from '@/lib/auth/client'
import { AuthCard } from './layout/auth-card'

export type MagicLinkParams = Parameters<typeof authClient.signIn.magicLink>[0]

interface ConfirmEmailAddressProps {
  magicLinkParams: MagicLinkParams
}
export function ConfirmEmailAddress({ magicLinkParams }: ConfirmEmailAddressProps) {
  const [isResending, setIsResending] = useState(false)
  const searchParams = useSearchParams()
  const returnUrl = searchParams.get('returnUrl')

  // Extract needed values for UI display
  const { email } = magicLinkParams
  const isSignUp = magicLinkParams?.fetchOptions?.body?.mode === 'signup'

  const handleResendMagicLink = async () => {
    try {
      setIsResending(true)

      // If returnUrl in searchParams has changed, use that instead
      const updatedParams = {
        ...magicLinkParams,
        callbackURL: returnUrl || magicLinkParams.callbackURL,
      }

      const { error } = await authClient.signIn.magicLink(updatedParams)

      if (error) throw new Error(error.message)

      toast.success(`Magic link resent! Check your email to ${isSignUp ? 'sign up' : 'sign in'}`)
    } catch (error) {
      console.error('Failed to resend magic link:', error)
      if (error instanceof Error) {
        toast.error(error.message || 'Failed to resend magic link')
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setIsResending(false)
    }
  }

  return (
    <AuthCard
      title="Check Your Email"
      description={`We've sent you a magic link to ${isSignUp ? 'sign up' : 'sign in'}.`}
      footer={
        <div className="text-center">
          <Button
            variant="ghost"
            size="sm"
            className="text-primary text-xs hover:bg-primary/10 hover:text-primary/90"
            onClick={handleResendMagicLink}
            isLoading={isResending}
            loadingText="Sending..."
          >
            Resend magic link
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center space-y-6 py-4">
        <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-foreground/5">
          <div className="absolute inset-0 rounded-full border-2 border-foreground/20" />
          <div className="absolute inset-2 rounded-full bg-gradient-to-br from-foreground/5 to-foreground/20" />
          <MailIcon className="relative h-12 w-12 text-foreground" strokeWidth={1.5} />
        </div>

        <div className="space-y-2 text-center">
          <p className="font-medium text-lg">{email}</p>
        </div>

        <div className="space-y-2 text-center text-muted-foreground text-sm">
          <p>
            Please check your email and click on the link to {isSignUp ? 'sign up' : 'sign in'}. You
            may need to check your spam folder.
          </p>
        </div>
      </div>
    </AuthCard>
  )
}
